/**
 * The CAPTCHA on the public auth forms (sign-up, sign-in, forgot-password).
 *
 * Cloudflare Turnstile and hCaptcha expose the same explicit-render API
 * (`render(el, {sitekey, callback, "expired-callback", "error-callback"})`,
 * `reset(id)`, `remove(id)`), so one widget serves both. Which one, and its
 * public site key, come from `GET /capabilities` — switching it on is an
 * operator `.env` change, never a console rebuild. The backend verifies the
 * token; a token is single-use, so the form bumps `resetKey` after every
 * submit that used one.
 */
import { useEffect, useRef, useState } from "react";
import { theme } from "antd";

import type { CaptchaProvider } from "@/lib/captcha";

interface CaptchaApi {
  render: (el: HTMLElement, options: Record<string, unknown>) => string;
  reset: (id?: string) => void;
  remove: (id: string) => void;
}

const SCRIPTS: Record<CaptchaProvider, { src: string; global: "turnstile" | "hcaptcha" }> = {
  turnstile: {
    src: "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit",
    global: "turnstile",
  },
  hcaptcha: { src: "https://js.hcaptcha.com/1/api.js?render=explicit", global: "hcaptcha" },
};

const loading = new Map<CaptchaProvider, Promise<CaptchaApi>>();

function apiFor(provider: CaptchaProvider): CaptchaApi | undefined {
  return (window as unknown as Record<string, CaptchaApi | undefined>)[SCRIPTS[provider].global];
}

function loadScript(provider: CaptchaProvider): Promise<CaptchaApi> {
  const ready = apiFor(provider);
  if (ready) return Promise.resolve(ready);
  let pending = loading.get(provider);
  if (!pending) {
    pending = new Promise<CaptchaApi>((resolve, reject) => {
      const script = document.createElement("script");
      script.src = SCRIPTS[provider].src;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        const api = apiFor(provider);
        if (api) resolve(api);
        else reject(new Error("CAPTCHA script loaded without its API"));
      };
      script.onerror = () => {
        loading.delete(provider);
        reject(new Error("CAPTCHA script failed to load"));
      };
      document.head.appendChild(script);
    });
    loading.set(provider, pending);
  }
  return pending;
}

interface Props {
  provider: CaptchaProvider;
  siteKey: string;
  onToken: (token: string | null) => void;
  /** Bump to clear the widget (a token is single-use). */
  resetKey?: number;
}

export default function CaptchaWidget({ provider, siteKey, onToken, resetKey = 0 }: Props) {
  const container = useRef<HTMLDivElement>(null);
  const widget = useRef<{ api: CaptchaApi; id: string } | null>(null);
  const onTokenRef = useRef(onToken);
  onTokenRef.current = onToken;
  const [failed, setFailed] = useState(false);
  const { token: themeToken } = theme.useToken();
  const dark = themeToken.colorBgContainer.toLowerCase() !== "#ffffff";

  useEffect(() => {
    let cancelled = false;
    loadScript(provider)
      .then((api) => {
        if (cancelled || !container.current) return;
        const id = api.render(container.current, {
          sitekey: siteKey,
          theme: dark ? "dark" : "light",
          callback: (value: string) => onTokenRef.current(value),
          "expired-callback": () => onTokenRef.current(null),
          "error-callback": () => onTokenRef.current(null),
        });
        widget.current = { api, id };
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
      if (widget.current) {
        try {
          widget.current.api.remove(widget.current.id);
        } catch {
          // already gone with the page
        }
        widget.current = null;
      }
    };
  }, [provider, siteKey, dark]);

  useEffect(() => {
    if (resetKey && widget.current) widget.current.api.reset(widget.current.id);
  }, [resetKey]);

  return (
    <div style={{ marginBottom: 24, minHeight: 65 }}>
      <div ref={container} data-testid="captcha-widget" />
      {failed ? (
        <div role="alert" style={{ color: themeToken.colorError }}>
          The security check couldn't load. Check your connection or disable content
          blockers for this page, then reload.
        </div>
      ) : null}
    </div>
  );
}
